import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserType } from "../../../utils/authutils";
import { useToast } from "../../shared/ToastProvider";
import { NOTIFICATION_DATE_MODE, NOTIFICATION_TITLE_SOURCE } from "../constants";
import { resolveNotificationKind, selectNotificationTitle } from "../model/notificationModel";
import { resolveNotificationPresentation } from "../model/notificationPresentation";
import { resolveNotificationDestination } from "../model/notificationRouting";
import { formatAbsoluteNotificationDate, formatNotificationTime } from "../model/notificationTime";
import { NotificationAvatar } from "./NotificationAvatar";
import { NotificationMessage } from "./NotificationMessage";

export function NotificationCard({
  notification,
  dateMode = NOTIFICATION_DATE_MODE.RELATIVE,
  onDismiss,
  onNavigate,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = getUserType();
  const { addToast } = useToast();
  const { t, i18n } = useTranslation();

  const language = i18n.language || "en";

  const kind = resolveNotificationKind(notification);
  const presentation = resolveNotificationPresentation(kind);

  const destination = resolveNotificationDestination(presentation.target, {
    userType,
    tenderId: notification.tenderId,
    projectId: notification.projectId,
    hasProjectAccess: notification.hasProjectAccess,
  });

  const dismissesOnClick = presentation.dismissesOnClick && !notification.isRead;

  const title = selectNotificationTitle(notification, presentation, { language, t });
  const showsTitle = presentation.titleSource !== NOTIFICATION_TITLE_SOURCE.NONE;
  const showsSubtitle = presentation.showsSubtitle && Boolean(notification.tenderName);

  const isInteractive = Boolean(destination) || dismissesOnClick;

  const displayDate = formatNotificationTime(notification.createdAt, {
    t,
    language,
    mode: dateMode,
  });
  const absoluteDate = formatAbsoluteNotificationDate(notification.createdAt, language);

  const markAsRead = useCallback(() => {
    if (!notification.isRead) onDismiss(notification.id);
  }, [notification.id, notification.isRead, onDismiss]);

  const handleClick = useCallback(() => {
    if (!isInteractive) return;

    if (dismissesOnClick) markAsRead();

    if (!destination) return;

    onNavigate?.();

    if (location.pathname + location.search !== destination) navigate(destination);
  }, [
    destination,
    dismissesOnClick,
    isInteractive,
    location.pathname,
    location.search,
    markAsRead,
    navigate,
    onNavigate,
  ]);

  return (
    <div
      data-testid="notification-card"
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (isInteractive && (event.key === "Enter" || event.key === " ")) handleClick();
      }}
      className={`bg-white rounded-lg p-4 w-full ${
        isInteractive ? "cursor-pointer hover:bg-gray-50" : ""
      }`}
    >
      <div className="wrap flex gap-2">
        <NotificationAvatar icon={presentation.icon} letter={notification.avatarLetter} />

        <div className="flex-1 flex flex-col gap-0 justify-center min-w-0">
          <div className="flex items-center justify-between gap-3">
            {showsTitle ? (
              <p className="justify-start text-base min-w-0">
                <span
                  className={`block truncate max-w-[160px] ${presentation.titleClassName}`}
                  title={title}
                >
                  {title}
                </span>
              </p>
            ) : (
              <span className="flex-1" />
            )}

            <p
              className="text-textSecondaryColor text-[10px] justify-end shrink-0 whitespace-nowrap"
              title={absoluteDate}
            >
              {displayDate}
            </p>
          </div>

          <div className="flex items-center min-w-0">
            <NotificationMessage
              notification={notification}
              t={t}
              addToast={addToast}
              language={language}
              onMarkRead={markAsRead}
            />
          </div>

          {showsSubtitle && (
            <div className="flex items-center">
              <p className="flex-1 justify-start text-xs text-gray-500 truncate">
                {notification.tenderName}
              </p>
            </div>
          )}
        </div>

        <div className="w-fit min-h-full px-5 grid place-items-center">
          <span
            data-testid="notification-unread-dot"
            aria-hidden="true"
            className={`block w-2 h-2 rounded-full ${
              notification.isRead ? "bg-transparent" : "bg-secondaryColor"
            }`}
          />
        </div>
      </div>
    </div>
  );
}
